import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Envelope, Phone, ChartLine, Eye, ArrowClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "replied", label: "Replied" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

export default function LeadInbox() {
  const { user } = useAuth();
  const [data, setData] = useState({ leads: [], stats: { total: 0, new: 0, views_7d: 0, views_30d: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/leads");
      setData(data);
      setLastRefresh(new Date());
      // tab title badge
      const unread = data.stats?.new || 0;
      if (typeof document !== "undefined") {
        document.title = unread > 0 ? `(${unread}) Lead Inbox · Dry Ice Connect` : "Lead Inbox · Dry Ice Connect";
      }
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30000);
    return () => {
      clearInterval(pollRef.current);
      if (typeof document !== "undefined") document.title = "Dry Ice Connect";
    };
  }, [load]);

  const updateStatus = async (lead_id, status) => {
    try {
      await api.patch(`/me/leads/${lead_id}`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (user && (user.level || 0) < 2) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="inbox-level-gate">
          <div className="section-label mb-3">— UPGRADE REQUIRED</div>
          <h1 className="font-chivo font-black text-4xl text-white uppercase tracking-tight">Lead Inbox is a Level 2 feature</h1>
          <p className="font-plex text-[#9CA3AF] mt-4">Upgrade to <strong className="text-[#00D4FF]">Network Pro</strong> to receive and manage customer leads from the US map.</p>
          <Link to="/billing?level=2" className="btn-primary mt-6 inline-flex" data-testid="inbox-upgrade-cta">Upgrade to Network Pro</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="section-label mb-2">— LEAD INBOX</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="inbox-headline">
              Your Customer Leads
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-[#52525B]" data-testid="inbox-last-refresh">
              {lastRefresh ? `Refreshed ${lastRefresh.toLocaleTimeString()}` : "—"}
            </span>
            <button onClick={load} className="btn-secondary text-xs" data-testid="inbox-refresh">
              <ArrowClockwise size={12} weight="bold" /> Refresh
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626] mb-6" data-testid="inbox-stats">
          <StatCard testid="stat-total" icon={Envelope} label="Total leads" value={data.stats.total} />
          <StatCard testid="stat-new" icon={Envelope} label="New & unread" value={data.stats.new} highlight />
          <StatCard testid="stat-views-7" icon={Eye} label="Views · 7d" value={data.stats.views_7d} />
          <StatCard testid="stat-views-30" icon={ChartLine} label="Views · 30d" value={data.stats.views_30d} />
        </div>

        {loading ? (
          <div className="font-mono text-xs text-[#52525B] text-center py-12">[LOADING]</div>
        ) : data.leads.length === 0 ? (
          <div className="surface p-12 text-center" data-testid="inbox-empty">
            <Envelope size={40} weight="duotone" className="text-[#00D4FF] mx-auto mb-3" />
            <div className="font-chivo font-bold text-white uppercase tracking-tight">No leads yet</div>
            <p className="font-plex text-sm text-[#9CA3AF] mt-2">
              When customers contact you from the map, they'll show up here. Make sure your listing is approved and your service area is set in <Link to="/dashboard/listing" className="text-[#00D4FF] hover:underline">My Listing</Link>.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="inbox-leads">
            {data.leads.map((l) => (
              <div key={l.id} className="surface p-5" data-testid={`inbox-lead-${l.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-chivo font-bold text-white truncate">{l.customer_name}</h3>
                      <span className={`tag ${l.status === "new" ? "tag-active" : ""}`} data-testid={`inbox-status-tag-${l.id}`}>
                        {(l.status || "new").toUpperCase()}
                      </span>
                      {l.preferred_contact && <span className="tag">PREFERS {l.preferred_contact.toUpperCase()}</span>}
                    </div>
                    <div className="font-mono text-xs text-[#52525B] mt-1">
                      {formatDate(l.created_at)} · zip {l.zip} · {l.job_type || "general"}
                    </div>
                  </div>
                  <select
                    value={l.status || "new"}
                    onChange={(e) => updateStatus(l.id, e.target.value)}
                    className="input-rugged text-xs py-1 w-32"
                    data-testid={`inbox-status-select-${l.id}`}>
                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                  {l.customer_email && (
                    <a href={`mailto:${l.customer_email}`} className="flex items-center gap-1 text-[#00D4FF] hover:underline" data-testid={`inbox-email-${l.id}`}>
                      <Envelope size={14} weight="duotone" /> {l.customer_email}
                    </a>
                  )}
                  {l.customer_phone && (
                    <a href={`tel:${l.customer_phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-1 text-[#00D4FF] hover:underline" data-testid={`inbox-phone-${l.id}`}>
                      <Phone size={14} weight="duotone" /> {l.customer_phone}
                    </a>
                  )}
                </div>

                {l.message && (
                  <blockquote className="border-l-2 border-[#00D4FF] pl-3 mt-3 font-plex text-sm text-[#9CA3AF] whitespace-pre-wrap">{l.message}</blockquote>
                )}
              </div>
            ))}
          </div>
        )}
        {error && <div className="font-mono text-xs text-[#EF4444] mt-4" data-testid="inbox-error">{error}</div>}
      </div>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div className="bg-[#0A0A0A] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 font-mono text-[10px] text-[#9CA3AF] tracking-widest uppercase">
        <Icon size={12} weight="duotone" className="text-[#00D4FF]" /> {label}
      </div>
      <div className={`font-chivo font-black text-3xl mt-1 tracking-tighter ${highlight ? "text-[#00D4FF]" : "text-white"}`}>{value}</div>
    </div>
  );
}
